import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Environment, EnvironmentService } from '@galaxy/core';
import { FileInfo, GalaxyUploaderService, UploadResponse } from '@vendasta/galaxy/uploader';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { SourceId } from '@vendasta/galaxy/chat-composer/src/chat-composer.component';
import { CONVERSATION_HOST_APP_INTERFACE_TOKEN } from '../../../../../../core/src/index';
import { MAX_FILES } from '../../../../../../core/src/lib/conversation.constants';

const VIDEO_MIME_TYPES = ['video/mp4', 'video/3gpp', 'video/3gp'];

import { isMockVideoEnabled } from '../mock-video-panel/mock-video-utils';

@Injectable()
export class InboxUploadService extends GalaxyUploaderService {
  conversationId$$: ReplaySubject<string> = new ReplaySubject<string>(1);
  conversationId$: Observable<string> = this.conversationId$$.asObservable();

  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);

  constructor(
    http: HttpClient,
    private readonly env: EnvironmentService,
  ) {
    super(http);
  }

  buildRequest(fileInfo: FileInfo): Observable<UploadResponse> {
    // TODO(TIF-21 Phase 1): Remove mockVideo query param and mock paths — mock intercept for video transcoding pipeline
    if (isMockVideoEnabled() && fileInfo.file && VIDEO_MIME_TYPES.includes(fileInfo.file.type)) {
      return of({
        data: { url: 'mock://video' },
      });
    }

    this.uploadUrl = 'https://conversation-demo.apigateway.co/upload-file';
    if (this.env.getEnvironment() === Environment.PROD) {
      this.uploadUrl = 'https://conversation-prod.apigateway.co/upload-file';
    }
    return combineLatest([this.conversationId$, this.hostAppInterface.getNamespace()]).pipe(
      take(1),
      switchMap(([conversationId, namespace]) => {
        const params = new HttpParams().set('conversationID', conversationId || '').set('namespaceID', namespace.id);
        const body = new FormData();
        body.append('file', fileInfo.file as Blob);
        return this.http.post(this.uploadUrl!, body, { withCredentials: true, params }) as Observable<UploadResponse>;
      }),
    );
  }
}

/**
 * Returns the max file size in bytes for a channel, optionally narrowed by file type.
 * @param chatSourceId
 * @param videoUploadEnabled
 * @param fileType
 */
export function getMaxFileSize(chatSourceId?: SourceId, videoUploadEnabled = false, fileType?: string): number {
  if (!chatSourceId) {
    return 0;
  }
  switch (chatSourceId) {
    case 'sms':
      if (fileType === 'application/pdf') {
        return 600 * 1024; // Twilio non-image MMS recommendation
      }
      if (
        fileType === 'image/jpeg' ||
        fileType === 'image/jpg' ||
        fileType === 'image/png'
      ) {
        return 20 * 1024 * 1024;
      }
      return videoUploadEnabled
        ? 250 * 1024 * 1024 // videos transcoded server-side
        : 5 * 1024 * 1024;
    case 'email':
      return 10 * 1024 * 1024; // 10MB
    default:
      return 250 * 1024 * 1024; // 250MB
  }
}

/**
 * Per-MIME-type max file size overrides for a channel, if applicable.
 * @param chatSourceId
 * @param videoUploadEnabled
 */
export function getMaxFileSizeByType(
  chatSourceId?: SourceId,
  videoUploadEnabled = false,
): Record<string, number> | undefined {
  if (chatSourceId === 'sms') {
    const size = (type: string) =>
      getMaxFileSize(chatSourceId, videoUploadEnabled, type);
    return {
      'application/pdf': size('application/pdf'),
      'image/jpeg': size('image/jpeg'),
      'image/jpg': size('image/jpg'),
      'image/png': size('image/png'),
    };
  }
  return undefined;
}

/**
 * Per-MIME-type max file count overrides for a channel, if applicable.
 * @param chatSourceId
 */
export function getMaxFilesByType(chatSourceId?: SourceId): Record<string, number> | undefined {
  if (chatSourceId === 'sms') {
    // single PDF attachment per message
    return { 'application/pdf': MAX_FILES };
  }
  return undefined;
}

/**
 * Returns the accepted file types for a given channel
 * @param chatSourceId
 */
export function getAcceptFileTypes(chatSourceId?: SourceId, videoUploadEnabled = false): string {
  if (!chatSourceId) {
    return '';
  }
  switch (chatSourceId) {
    case 'sms':
      return videoUploadEnabled
        ? 'image/jpeg, image/jpg, image/png, image/gif, application/pdf, video/mp4, video/quicktime, video/3gpp'
        : 'image/jpeg, image/jpg, image/png, image/gif, application/pdf';
    case 'whatsapp':
      return [
        'image/jpeg',
        'image/png',
        'video/3gp',
        'video/mp4',
        'text/plain',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/pdf',
      ].join(', ');
    case 'openai':
    case 'ai-assistant':
      return 'image/jpeg, image/jpg, image/png, image/gif, image/webp';
    default:
      return '*/*';
  }
}

function formatBytes(bytes: number): string {
  const mb = bytes / 1024 / 1024;
  if (mb >= 1) {
    return `${Number.isInteger(mb) ? mb : Number(mb.toFixed(2))} MB`;
  }
  const kb = bytes / 1024;
  return `${Number.isInteger(kb) ? kb : Number(kb.toFixed(2))} KB`;
}

export function getMaxFileSizeErrorDisplay(
  chatSourceId?: SourceId,
  videoUploadEnabled = false,
  fileType?: string,
): string {
  const bytes = getMaxFileSize(
    chatSourceId, videoUploadEnabled, fileType,
  );
  if (!bytes) {
    return '';
  }
  const display = formatBytes(bytes);
  if (
    chatSourceId === 'sms' &&
    fileType === 'image/gif'
  ) {
    return `${display} (GIF limit). ` +
      'JPEG and PNG support up to 20 MB';
  }
  return display;
}
